/**
 * Careers (canvas: Careers, Careers a head-office role, Careers something to fix, Careers sent): the application that
 * replaces the Google form. The stores are Cabinet's own (the hub's store list), so a new site is a choice the day it's
 * added. The resume goes straight into the platform's private bucket through a one-off upload link (careers/upload), then
 * the application names it (careers/apply), which saves it and emails whoever hires for the role.
 */
import { hub } from "./ms-hub.js";

const STATE_ORDER = ["NSW", "VIC", "QLD", "ACT", "SA", "WA", "TAS", "NT"];
const STORE_ROLES = new Set(["store-team", "store-managers"]);
const WHEN_ROLES = new Set(["store-team", "events"]);
const MAX = 10 * 1024 * 1024;
const TYPES = { pdf: "application/pdf", doc: "application/msword", docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" };
const EMAIL = /^[^\s@<>()",;]{1,64}@[^\s@<>()",;]+\.[a-z]{2,}$/i;

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
const label = (name) => String(name || "").replace(/\s+Messina$/i, "").trim();
export const andList = (xs) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`);

/** Cabinet's sites by state, in the order the site lists states, each named without "Messina". Test sites never. */
export function groupStores(stores) {
  const by = new Map();
  for (const s of stores || []) {
    const name = label(s?.name);
    if (!name || /^test\s/i.test(String(s.name)) || !s.id) continue;
    const st = String(s.state || "").toUpperCase();
    if (!by.has(st)) by.set(st, []);
    by.get(st).push({ id: String(s.id), name });
  }
  const rank = (st) => (STATE_ORDER.indexOf(st) === -1 ? 99 : STATE_ORDER.indexOf(st));
  return [...by.entries()].sort((a, b) => rank(a[0]) - rank(b[0])).map(([st, rows]) => [st, rows.sort((a, b) => a.name.localeCompare(b.name))]);
}

/** Which parts a role asks for. */
export const rolePlan = (role) => ({ stores: STORE_ROLES.has(role), when: WHEN_ROLES.has(role) });

/** What's wrong with a chosen file, in words, or null. */
export function fileProblem(f) {
  const ext = (String(f?.name || "").match(/\.([A-Za-z0-9]{1,5})$/)?.[1] || "").toLowerCase();
  if (!TYPES[ext] || (f.type && !Object.values(TYPES).includes(f.type))) return `${f?.name || "That file"} isn’t a PDF or Word file. Save it as a PDF and add it again.`;
  if (!f.size) return `${f.name} looks empty. Try saving it again.`;
  if (f.size > MAX) return `${f.name} is too big. Your resume needs to be under 10MB.`;
  return null;
}

/** Everything still needed, in the order the form asks it, each with the field it points at. */
export function checkForm(v) {
  const out = [];
  const add = (field, message, short) => out.push({ field, message, short });
  if (!v.role) add("role", "Pick what you’re after.", "What you’re after");
  if (rolePlan(v.role).stores && !(v.stores || []).length) add("stores", "Pick at least one store.", "Which stores");
  if (!String(v.first || "").trim()) add("first", "Add your first name.", "Your first name");
  if (!String(v.last || "").trim()) add("last", "Add your last name.", "Your last name");
  if (!EMAIL.test(String(v.email || "").trim())) add("email", "That email doesn’t look right. Check it has a full stop after the @.", "Your email");
  if (!/^\+?[0-9 ()-]{6,40}$/.test(String(v.mobile || "").trim())) add("mobile", "That mobile number doesn’t look right.", "Your mobile");
  if (!v.hours) add("hours", "Pick the hours you’re after.", "The hours you’re after");
  if (!v.rights) add("rights", "Say whether you can work in Australia.", "Whether you can work in Australia");
  if (!v.resume) add("cv", v.uploading ? "Your resume is still adding. Give it a moment." : "Add your resume.", "Your resume");
  return out;
}

export function goesLine(role, roleName, storeNames) {
  if (role === "store-team") return storeNames.length ? `This goes to ${andList(storeNames)}.` : "This goes to the stores you pick.";
  return `This goes straight to the people who hire for ${String(roleName || "this role").toLowerCase()}.`;
}

export function doneWords({ first, role, stores, file }) {
  const where = role === "Store team" && stores.length ? andList(stores) : "the people who hire for it";
  return { heading: `Thanks, ${first}. It’s sent.`, line: `Your ${role} application went to ${where}, with your resume (${file}). If there’s a fit, they’ll be in touch.` };
}

const kb = (n) => (n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.max(1, Math.round(n / 1024))}KB`);

if (typeof window !== "undefined" && typeof customElements !== "undefined") {
  const ICON = (name) => document.querySelector(`template[data-cr-icon="${name}"]`)?.innerHTML || "";

  class MsCareers extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector("form");
      this.resume = null;
      this.uploading = false;
      this.stores = [];
      this.form.addEventListener("change", (e) => this.changed(e));
      this.form.addEventListener("submit", (e) => { e.preventDefault(); this.send(); });
      this.querySelector("[data-again]")?.addEventListener("click", () => this.again());
      this.querySelector("[data-cr-pills]")?.addEventListener("click", (e) => { const b = e.target.closest("[data-state]"); if (b) this.showState(b.dataset.state); });
      this.plan();
      this.loadStores();
    }

    async loadStores() {
      const box = this.querySelector("[data-cr-states]");
      const r = await hub("stores", {});
      const groups = r.ok ? groupStores(r.data?.stores) : [];
      if (!groups.length) {
        box.innerHTML = `<p class="t-small" data-st-fail>We couldn’t load the stores just now. <button class="link" type="button" data-st-retry>Try again</button></p>`;
        box.querySelector("[data-st-retry]").addEventListener("click", () => { box.innerHTML = `<p class="t-small t-soft">Loading the stores…</p>`; this.loadStores(); });
        return;
      }
      this.stores = groups.flatMap(([, rows]) => rows);
      const chip = (s) => `<label class="chip check-chip"><input type="checkbox" name="stores" value="${esc(s.id)}" data-name="${esc(s.name)}"><span class="tick">${ICON("check")}</span>${esc(s.name)}</label>`;
      box.innerHTML = groups.map(([st, rows], i) => `<fieldset class="ask cr-state${i === 0 ? " on" : ""}" data-group="${esc(st)}"><legend class="t-label">${esc(st)}</legend><div class="chips">${rows.map(chip).join("")}</div></fieldset>`).join("");
      this.querySelector("[data-cr-pills]").innerHTML = groups.map(([st], i) => `<button class="chip${i === 0 ? " on" : ""}" type="button" data-state="${esc(st)}" aria-pressed="${i === 0}">${esc(st)}</button>`).join("");
    }

    showState(st) {
      this.querySelectorAll("[data-state]").forEach((b) => { const on = b.dataset.state === st; b.classList.toggle("on", on); b.setAttribute("aria-pressed", String(on)); });
      this.querySelectorAll("[data-group]").forEach((g) => g.classList.toggle("on", g.dataset.group === st));
    }

    values() {
      const f = new FormData(this.form);
      return {
        role: f.get("role") || "", stores: f.getAll("stores"), first: f.get("first"), last: f.get("last"), email: f.get("email"), mobile: f.get("mobile"),
        hours: f.get("hours") || "", availability: f.getAll("when"), rights: f.get("rights") || "", note: f.get("note") || "", resume: this.resume, uploading: this.uploading,
      };
    }

    picked() { return [...this.form.querySelectorAll('input[name="stores"]:checked')].map((i) => i.dataset.name); }
    roleName() { return this.form.querySelector('input[name="role"]:checked')?.dataset.title || ""; }

    /** Shows the parts the picked role asks for and numbers them in order. */
    plan() {
      const role = this.form.querySelector('input[name="role"]:checked')?.value || "";
      const p = rolePlan(role);
      const step = this.querySelector('[data-step="stores"]');
      step.hidden = !p.stores;
      step.querySelectorAll("input").forEach((i) => (i.disabled = !p.stores));
      const when = this.querySelector("[data-when]");
      when.hidden = !p.when;
      when.querySelectorAll("input").forEach((i) => (i.disabled = !p.when));
      let n = 0;
      this.querySelectorAll("[data-step]").forEach((s) => { if (!s.hidden) s.querySelector(".n").textContent = String(++n); });
      const pickedLine = this.querySelector("[data-picked]");
      const names = this.picked();
      pickedLine.hidden = !names.length;
      pickedLine.innerHTML = `<strong>Picked:</strong> ${esc(andList(names))}`;
      this.querySelector("[data-goes]").textContent = goesLine(role, this.roleName(), names);
    }

    changed(e) {
      const t = e.target;
      if (t.name === "role" || t.name === "stores") this.plan();
      if (t.name === "rights") this.querySelector("[data-rights-no]").hidden = t.value !== "no";
      if (t.type === "file" && t.files?.[0]) this.upload(t.files[0]);
      if (t.getAttribute("aria-invalid") === "true") this.clearError(t.name === "role" ? "role" : t.name === "stores" ? "stores" : t.id?.replace(/^cf-/, "") || t.name);
    }

    drop(state, f, extra = "") {
      const box = this.querySelector("[data-drop]");
      const input = `<input type="file" class="sr" id="cf-cv" name="cv" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"${state === "bad" ? ' aria-invalid="true" aria-describedby="cf-cv-err"' : ""}>`;
      if (state === "busy") {
        box.innerHTML = `<div class="drop got"><span class="drop-ic">${ICON("upload")}</span><span class="stack" style="gap: 8px; flex: 1"><span class="t-card">${esc(f.name)}</span>`
          + `<span class="bar light" role="progressbar" aria-label="Adding your resume" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0"><i style="width: 0%"></i></span><span class="t-small t-soft">Adding it…</span></span></div>`;
      } else if (state === "done") {
        box.innerHTML = `<div class="drop got"><span class="drop-ic">${ICON("file-text")}</span><span class="stack" style="gap: 4px"><span class="t-card">${esc(f.name)}</span><span class="t-small t-soft">${esc(f.name.split(".").pop().toUpperCase())}, ${kb(f.size)}. Added</span></span>`
          + `<label class="btn ghost sm">${input}Change</label></div>`;
      } else {
        box.innerHTML = `<label class="drop${state === "bad" ? " bad" : ""}">${input}<span class="drop-ic">${ICON("upload")}</span><span class="stack" style="gap: 4px"><span class="t-card">Add your resume</span><span class="t-small t-soft">PDF or Word, up to 10MB</span></span><span class="btn ghost sm">Choose a file</span></label>`;
      }
      const err = this.querySelector("#cf-cv-err");
      err.hidden = state !== "bad";
      err.innerHTML = state === "bad" ? `${ICON("circle-alert")}${extra}` : "";
      this.querySelector("[data-cv-say]").textContent = state === "busy" ? "Adding your resume." : state === "done" ? `${f.name} added.` : "";
    }

    async upload(f) {
      const problem = fileProblem(f);
      this.resume = null;
      if (problem) { this.drop("bad", f, esc(problem)); return; }
      this.uploading = true;
      this.drop("busy", f);
      const fail = () => {
        this.uploading = false;
        const back = this.dataset.fallback;
        this.drop("bad", f, `We couldn’t add it just now. Try again${back ? `, or <a href="${esc(back)}">apply on our old form</a>` : ""}.`);
      };
      const r = await hub("careers/upload", { name: f.name, type: f.type, size: f.size });
      if (!r.ok || !r.data?.url || !r.data?.path) { if (r.message) { this.uploading = false; this.drop("bad", f, esc(r.message)); } else fail(); return; }
      const ok = await new Promise((resolve) => {
        const x = new XMLHttpRequest();
        x.open("PUT", r.data.url);
        x.upload.onprogress = (e) => {
          if (!e.lengthComputable) return;
          const pc = Math.round((e.loaded / e.total) * 100);
          const bar = this.querySelector("[data-drop] .bar");
          if (bar) { bar.setAttribute("aria-valuenow", String(pc)); bar.querySelector("i").style.width = `${pc}%`; }
        };
        x.onload = () => resolve(x.status >= 200 && x.status < 300);
        x.onerror = () => resolve(false);
        const body = new FormData();
        body.append("cacheControl", "3600");
        body.append("", f, f.name);
        x.send(body);
      });
      if (!ok) { fail(); return; }
      this.uploading = false;
      this.resume = { path: r.data.path, name: f.name };
      this.file = f;
      this.drop("done", f);
      this.clearError("cv");
    }

    // A field's inputs: the input itself, or every choice in a group (role, stores, hours, rights), so each one says it's wrong.
    inputs(field) {
      const own = this.querySelector(`#cf-${field}`);
      if (own && own.matches("input, textarea, select")) return [own];
      return [...this.querySelectorAll(`#cf-${field} input, [data-field="${field}"] input`)];
    }

    clearError(field) {
      for (const el of this.inputs(field)) { el.removeAttribute("aria-invalid"); el.removeAttribute("aria-describedby"); }
      const err = this.querySelector(`#cf-${field}-err`);
      if (err) { err.hidden = true; err.textContent = ""; }
    }

    showErrors(errors) {
      const sum = this.querySelector("[data-err-sum]");
      this.querySelectorAll("[aria-invalid]").forEach((x) => x.removeAttribute("aria-invalid"));
      this.querySelectorAll(".err[id^='cf-']").forEach((x) => { x.hidden = true; });
      for (const e of errors) {
        const err = this.querySelector(`#cf-${e.field}-err`);
        if (err) { err.innerHTML = `${ICON("circle-alert")}${esc(e.message)}`; err.hidden = false; }
        for (const el of this.inputs(e.field)) { el.setAttribute("aria-invalid", "true"); el.setAttribute("aria-describedby", `cf-${e.field}-err`); }
      }
      const words = ["", "One thing", "Two things", "Three things", "Four things", "Five things"][errors.length] || `${errors.length} things`;
      sum.querySelector("[data-err-count]").textContent = `${words} to fix before it can go`;
      sum.querySelector("[data-err-list]").innerHTML = errors.map((e) => `<li><a href="#cf-${esc(e.field)}" data-err-to="${esc(e.field)}">${esc(e.short || e.message)}</a></li>`).join("");
      // A link in the summary puts you in the field itself (its first choice, for a group), not just near it.
      sum.querySelectorAll("[data-err-to]").forEach((a) => a.addEventListener("click", (ev) => {
        const first = this.inputs(a.dataset.errTo).find((x) => !x.disabled);
        if (!first) return;
        ev.preventDefault();
        (first.closest("fieldset, .field") || first).scrollIntoView({ block: "center" });
        first.focus({ preventScroll: true });
      }));
      sum.hidden = false;
      sum.focus();
    }

    async send() {
      const v = this.values();
      const errors = checkForm(v);
      if (errors.length) { this.showErrors(errors); return; }
      this.querySelector("[data-err-sum]").hidden = true;
      const say = this.querySelector("[data-say]");
      const btn = this.querySelector("[data-send]");
      btn.disabled = true;
      say.textContent = "Sending…";
      const r = await hub("careers/apply", {
        role: v.role, stores: rolePlan(v.role).stores ? v.stores : [], firstName: v.first, lastName: v.last, email: v.email, mobile: v.mobile,
        hours: v.hours, availability: rolePlan(v.role).when ? v.availability : [], rights: v.rights, note: v.note, resume: v.resume,
      }, { timeoutMs: 30000 });
      btn.disabled = false;
      if (r.ok) {
        const d = doneWords({ first: String(v.first).trim(), role: this.roleName(), stores: rolePlan(v.role).stores ? this.picked() : [], file: v.resume.name });
        say.textContent = "";
        this.querySelector("[data-careers-form]").hidden = true;
        const done = this.querySelector("[data-careers-done]");
        done.querySelector("[data-done-h]").textContent = d.heading;
        done.querySelector("[data-done-p]").textContent = d.line;
        done.hidden = false;
        done.querySelector("[data-done-h]").focus();
        return;
      }
      if (r.reason === "too-many") { say.textContent = "You’ve sent a few today already. Try again tomorrow."; return; }
      if (r.message) { say.textContent = ""; this.showErrors([{ field: /resume/i.test(r.message) ? "cv" : /store/i.test(r.message) ? "stores" : "role", message: r.message }]); return; }
      const back = this.dataset.fallback;
      say.innerHTML = `We couldn’t send it just now. Try again in a minute${back ? `, or <a href="${esc(back)}">apply on our old form</a>` : ""}.`;
    }

    /** Apply again: a new role and resume, the same person. */
    again() {
      this.form.querySelectorAll('input[name="role"], input[name="stores"]').forEach((i) => (i.checked = false));
      this.resume = null;
      this.drop("empty");
      this.plan();
      this.querySelector("[data-careers-done]").hidden = true;
      this.querySelector("[data-careers-form]").hidden = false;
      this.form.querySelector('input[name="role"]')?.focus();
    }
  }
  if (!customElements.get("ms-careers")) customElements.define("ms-careers", MsCareers);
}
